import { LEGACY_ONLY } from "./killSwitch";
// GUNDI-5639 / GUNDI-5642 / GUNDI-5665: which UI tree renders.
//
// "new" (default/absent) is the current portal; "legacy" is the frozen
// pre-flow-map portal under src/legacy/, which users opt into from the banner
// strip in the new tree. The routing IS the switch — no boot guards, no
// cross-page redirects — so the value is read at render time and subscribers
// re-render when it flips. Removed at sunset (Phase 5) together with
// src/legacy/.
export type UiVersion = "new" | "legacy";

// storage: localStorage (GUNDI-5665) — the legacy opt-out must survive the tab
// and the session; "new" is the default and is stored as the ABSENCE of the key.
export const UI_VERSION_KEY = "gundi_ui_version";

// One-time migration (GUNDI-5665 review finding): GUNDI-5639 used this SAME
// key, store and semantics, and GUNDI-5642 never removed it — so an opt-out
// clicked against that much older build would silently pin its user to legacy
// on day one of "new is the default for everyone". Reset the key once; the
// marker protects every choice made from this release onward. Anyone who
// really wants legacy is one banner click away.
const MIGRATION_KEY = "gundi_ui_default_migrated";
try {
    if (!localStorage.getItem(MIGRATION_KEY)) {
        localStorage.removeItem(UI_VERSION_KEY);
        localStorage.setItem(MIGRATION_KEY, "1");
    }
} catch {
    // No storage: nothing stale to migrate either.
}

// Last value set in this page view, used whenever storage cannot be trusted to
// answer for it — storage disabled, private mode, or a quota-full store whose
// reads still work while writes throw. Without this a switch in any of those
// cases would be a no-op: the write fails, and the read that follows reports
// whatever stale/absent value the store holds.
let inMemoryVersion: UiVersion | null = null;
let persisted = true;

export function getUiVersion(): UiVersion {
    if (LEGACY_ONLY) return "legacy";
    // A failed write means the store no longer reflects the user's choice, even
    // if reads keep succeeding.
    if (!persisted && inMemoryVersion) return inMemoryVersion;
    try {
        return localStorage.getItem(UI_VERSION_KEY) === "legacy" ? "legacy" : "new";
    } catch {
        return inMemoryVersion ?? "new";
    }
}

const listeners = new Set<() => void>();

export function setUiVersion(version: UiVersion) {
    inMemoryVersion = version;
    try {
        if (version === "legacy") localStorage.setItem(UI_VERSION_KEY, "legacy");
        // "new" is the default, so it is stored as the ABSENCE of the key —
        // one less orphan to clean up at sunset.
        else localStorage.removeItem(UI_VERSION_KEY);
        persisted = true;
    } catch {
        // Not persisted (the choice lasts this page view only), but the notify
        // below still swaps the tree so the click is never a no-op.
        persisted = false;
    }
    listeners.forEach((l) => l());
}

// No longer called on logout (GUNDI-5665); kept for the sunset cleanup.
//
// Deliberately does NOT notify subscribers: it was only ever used on a page
// that was on its way out, and swapping the tree here would mount a whole
// portal tree — navbar, EULA and user-details requests — for the instant
// before the redirect lands.
export function clearUiVersion() {
    inMemoryVersion = null;
    persisted = true;
    try {
        localStorage.removeItem(UI_VERSION_KEY);
    } catch {
        // No storage: the in-memory reset above is the whole job.
    }
}

export function subscribeUiVersion(listener: () => void) {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
}
